import React, { useEffect, useRef, useState } from 'react';

import { Alert, AlertTitle, Button, LinearProgress, Stack, Typography } from '@mui/material';

import { useUpdateReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useDryRunReportingLeLensDataset } from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type { Lens, LensDataset } from '~/types/leLens';

import { declaredDataset } from './DeclareNow';

/**
 * F10 (01_USER_FLOWS.md): "On first open a banner says 'Upgrading N cards to datasets' while a
 * dry run backfill runs per dataset and per own-SQL card." A version 1 lens is normalised to
 * version 2 on read (the backend's GET handler), which mints a one-card dataset per own-SQL card
 * but cannot type it (no BigQuery access in that path): see A3's normalise.ts. Those datasets land
 * with `sql` and no `columns`, exactly 02_RIGHT_PANEL_SPEC.md's "not yet declared" state. This is
 * the whole-lens counterpart to DeclareNow.tsx's single-dataset "Declare now": on a Builder's first
 * open of such a lens it dry-runs every undeclared dataset and saves them all in one lens update,
 * so a Builder never has to click "Declare now" once per card by hand.
 *
 * A production lens that has already been through the backend's backfill CLI, and every local
 * lens in this build (A3's lane report: all 8 already read as version 2 with declared columns),
 * never has an undeclared dataset to begin with, so this renders nothing for them. It exists for
 * whatever the CLI has not reached yet, and for the CLI's own refusals (a dataset whose SQL fails
 * its dry run, e.g. because the table moved), which surface here as a per-dataset failure a
 * Builder can retry once the SQL is fixed, per F10's failure branch.
 *
 * Nothing on the canvas depends on this: every card's own `dataset.sql` still runs to load its
 * data whether or not `columns` is declared. This banner only unblocks the *editing* surface
 * (the panel's shelves and field list need declared columns to bind against), so it renders for
 * the dashboard owner/editor only; a Viewer's canvas is unaffected either way.
 */

export type UpgradeBannerProps = {
  lens: Lens;
  /** Only the dashboard owner/editor can trigger a dry run and a save; a Viewer never sees this,
   *  matching F10's "(Builder)" scope. Pass `access.canEdit` from the page's own lens fetch. */
  canEdit: boolean;
  /** Runs the backfill automatically once per lens id when true (the real mount). A caller that
   *  wants to inspect the mid-run or failed state without waiting on the effect (a test, the
   *  visual harness) can set this false and call the exposed `run` prop instead. */
  autoRun?: boolean;
};

export type DeclareFailure = { datasetId: string; datasetName: string; cardTitles: Array<string>; error: string };

function undeclaredDatasetsOf(lens: Lens): Array<LensDataset> {
  return (lens.spec.datasets ?? []).filter((dataset) => !dataset.columns?.length);
}

function cardTitlesOn(lens: Lens, datasetId: string): Array<string> {
  return lens.spec.cards.filter((card) => card.datasetId === datasetId).map((card) => card.title);
}

export default function UpgradeBanner({ lens, canEdit, autoRun = true }: UpgradeBannerProps) {
  const dryRun = useDryRunReportingLeLensDataset();
  const updateLens = useUpdateReportingLeLens();
  const [running, setRunning] = useState(false);
  const [failures, setFailures] = useState<Array<DeclareFailure>>([]);
  const ranForLensId = useRef<string | null>(null);

  const undeclared = undeclaredDatasetsOf(lens);
  const cardCount = undeclared.reduce((sum, dataset) => sum + Math.max(cardTitlesOn(lens, dataset.id).length, 1), 0);

  const run = async () => {
    const targets = undeclaredDatasetsOf(lens);
    if (!targets.length) return;

    setRunning(true);
    const outcomes = await Promise.allSettled(targets.map((dataset) => dryRun.mutateAsync(dataset.sql)));

    const nextFailures: Array<DeclareFailure> = [];
    const nextDatasets = [...(lens.spec.datasets ?? [])];
    let declaredCount = 0;
    outcomes.forEach((outcome, index) => {
      const dataset = targets[index];
      if (outcome.status === 'fulfilled') {
        declaredCount += 1;
        const position = nextDatasets.findIndex((candidate) => candidate.id === dataset.id);
        if (position >= 0) nextDatasets[position] = declaredDataset(dataset, outcome.value);
        return;
      }
      nextFailures.push({
        datasetId: dataset.id,
        datasetName: dataset.name,
        cardTitles: cardTitlesOn(lens, dataset.id),
        error: outcome.reason instanceof Error ? outcome.reason.message : 'This dataset’s SQL could not be dry-run.',
      });
    });

    if (declaredCount > 0) {
      try {
        await updateLens.mutateAsync({
          id: lens.id,
          title: lens.title,
          description: lens.description,
          spec: { ...lens.spec, datasets: nextDatasets },
          version: lens.version,
          successMessage: `${declaredCount} of ${targets.length} dataset${targets.length === 1 ? '' : 's'} upgraded`,
          // Every dry run above already read each dataset's own SQL: nothing on the canvas
          // changes, so there is no reason to bill BigQuery again for a card re-run.
          refetchData: false,
        });
      } catch (err) {
        // The save that would have persisted every successful dry run above failed (a stale
        // version, a dropped connection): none of it landed, so every dataset that dry-ran
        // clean is still reported here, not only the ones that failed their own dry run.
        const saveError = err instanceof Error ? err.message : 'Could not save the declared columns.';
        targets.forEach((dataset) => {
          if (nextFailures.some((failure) => failure.datasetId === dataset.id)) return;
          nextFailures.push({
            datasetId: dataset.id,
            datasetName: dataset.name,
            cardTitles: cardTitlesOn(lens, dataset.id),
            error: saveError,
          });
        });
      }
    }

    setFailures(nextFailures);
    setRunning(false);
  };

  useEffect(() => {
    if (!autoRun || !canEdit || ranForLensId.current === lens.id || !undeclaredDatasetsOf(lens).length) return;
    ranForLensId.current = lens.id;
    void run();
    // Re-fires only for a different lens id: `lens` itself is refetched on the page's own
    // schedule and a fresh save from `run()` hands back a new object for the same id, which
    // would otherwise start this over on every one of its own successful saves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens.id, canEdit, autoRun]);

  if (!canEdit) return null;

  if (running) {
    return (
      <Alert icon={false} severity="info" sx={{ mb: 2 }}>
        <AlertTitle>
          Upgrading {cardCount} card{cardCount === 1 ? '' : 's'} to datasets
        </AlertTitle>
        <LinearProgress sx={{ mt: 1 }} />
      </Alert>
    );
  }

  if (failures.length > 0) {
    return (
      <Alert
        action={
          <Button color="inherit" onClick={() => void run()} size="small">
            Retry
          </Button>
        }
        severity="warning"
        sx={{ mb: 2 }}
      >
        <AlertTitle>
          {failures.length} dataset{failures.length === 1 ? '' : 's'} could not be upgraded
        </AlertTitle>
        <Stack spacing={0.5}>
          {failures.map((failure) => (
            <Typography key={failure.datasetId} variant="body2">
              {(failure.cardTitles.length ? failure.cardTitles.join(', ') : failure.datasetName) + ': ' + failure.error}
            </Typography>
          ))}
        </Stack>
      </Alert>
    );
  }

  return null;
}
